/* eslint-disable no-restricted-globals */ // $FlowFixMe[untyped-import]
import { createAction, createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import { selectFromConfig } from '@gandi/redux-config';
import { selectCurrent } from '@gandi/redux-sharing';
import { localeSelector as selectLocale } from '@gandi/translate';
/**
 * Selectors
 */

const selectAuthenticationState = state => state.authentication;

/**
 * Return true if a user is found in the state.
 */
export const selectIsAuthenticatedUser = state => !!state.authentication.userInfos && Object.keys(state.authentication.userInfos).length > 0;

/**
 * Return Authenticated User Infos.
 */
export const selectAuthenticatedUserInfos = state => state.authentication.userInfos;

/**
 * Return true if the current user has cc2 or god roles (staff member).
 */
export const selectIsPrivilegedUser = createSelector(selectAuthenticatedUserInfos, infos => {
  const roles = infos?.roles ?? [];
  return roles.indexOf('cc2') > -1 || roles.indexOf('god') > -1;
});

/**
 * Return true if the current user doesn't have the 'user_v5' role.
 * This means the user was migrated from a v4 handle, not created on v5 directly.
 */
export const selectIsMigratedUser = createSelector(selectAuthenticatedUserInfos, infos => {
  const roles = infos?.roles ?? [];
  return roles.indexOf('user_v5') === -1;
});

/**
 * Return true if the current user is logged as a SAML user.
 */
export const selectIsSamlUser = createSelector(selectAuthenticatedUserInfos, user => !!user && (user.username.includes('@') || user.roles.includes('saml')));

/**
 * Return true if the current user is being sudoed by a staff user.
 */
export const selectIsSudoedUser = createSelector(selectAuthenticatedUserInfos, user => !!user?.sudo);
const EMPTY_OBJECT = {};
/**
 * @private Private selector; use the public `selectFeatureFlag` selector
 * to correctly select a specific flag.
 */
const selectConfigFlags = selectFromConfig('flags', EMPTY_OBJECT);
/**
 * Retrieve the value of a feature flag.
 * Usage:
 * selectFeatureFlag(state, { featureFlagName: 'FF_MFA_EMAIL' })
 *
 * The feature flag can be enabled either globally in the conf of the app
 * (e.g.: `shared.flags.FF_MFA_EMAIL` in config.yml)
 * or for some users with the corresponding role
 * (e.g.: role `ff-mfa-email`).
 *
 * See https://gandi.gndoc.net/admin-v5/docs/guides/feature_flag/
 */
export const selectFeatureFlag = createSelector([selectConfigFlags, selectAuthenticatedUserInfos, (_, {
  featureFlagName
}) => featureFlagName], (flags, userInfos, featureFlagName) => {
  /**
   * Role as seen in the Peyotl BO
   * <https://bo-peyotl.{stage|corp}.gandi.net/users/{username}>.
   */
  const ffRole =
  // $FlowFixMe[incompatible-cast]
  featureFlagName.toLowerCase().replaceAll('_', '-');
  const ffConfName =
  // $FlowFixMe[incompatible-cast]
  featureFlagName.toUpperCase().replaceAll('-', '_');

  // Check if the feature flag is forced in the URL
  let hasFFInURL;
  if (typeof location !== 'undefined') {
    try {
      const currentLocation = new URL(location.href);
      if (currentLocation.searchParams.has(ffRole) || currentLocation.searchParams.has(ffConfName)) {
        const ffInURL = currentLocation.searchParams.has(ffRole) ? currentLocation.searchParams.get(ffRole) : currentLocation.searchParams.get(ffConfName);
        // If the bypass flag is present, any value (even empty, like `?ff-something`),
        // is considered truthy unless explicitly set to false.
        hasFFInURL = !['0', 'false', 'off', 'no', null].includes(ffInURL);
      }
    } catch (e) {
      // new URL() failed, probably because location.href is not a full URL
    }
  }
  return hasFFInURL !== undefined ? hasFFInURL :
  // Enabled globally in the conf of the curent environment
  !!flags[ffConfName] ||
  // or enabled for the current user via the corresponding role
  !!userInfos?.roles.includes(ffRole);
});

/**
 * @private Please use the public `selectHelpdeskUrl` selector.
 */
const selectBaseHelpdeskUrl = selectFromConfig('app.layout.urls.helpdesk', 'https://helpdesk.gandi.net/hc');
function getZdLocale(locale) {
  switch (locale) {
    case 'zh-hans':
    case 'zh-hant':
      return 'zh-tw';
    case 'en':
      return 'en-us';
    default:
      return locale;
  }
}
/**
 * Return the URL of the helpdesk, authenticated if possible.
 *
 * @param to optional string - the path to redirect to after authentication
 * @example selectHelpdeskUrl(state, 'requests/new?tf_13618597122204=link_domain')
 * @returns <https://helpdesk.gandi.net/hc/en-us/requests/new?tf_13618597122204=link_domain>
 */
export const selectHelpdeskUrl = createSelector([selectBaseHelpdeskUrl, selectLocale, (state, to) => to, selectIsPrivilegedUser, selectIsAuthenticatedUser], (baseUrl, locale, to, isPrivileged, isAuthenticated) => {
  const lang = getZdLocale(locale);
  const urlWithLocale = [baseUrl, lang, to].filter(Boolean).join('/');
  const urlWithSignin = [baseUrl, lang, `signin?return_to=${encodeURIComponent(urlWithLocale)}`].filter(Boolean).join('/');
  return isPrivileged || !isAuthenticated ?
  // return directly for privileged users to avoid infinite redirection on zendesk signin
  urlWithLocale :
  // redirect Gandi-authenticated users to the signin redirection callback
  urlWithSignin;
});

/**
 * Return the country code
 * If the found country code is invalid, return the fallback.
 */
const invalidCountryCodes = ['Unknown', 'ZZ'];
export const selectUserCountryCode = createSelector(selectAuthenticationState, selectFromConfig('defaultCountryCode', 'US'), (state, defaultCountryCode) => {
  // $FlowFixMe[sketchy-null-string]
  const countryCode = state.countryCode || defaultCountryCode;
  return invalidCountryCodes.indexOf(countryCode) === -1 ? countryCode : defaultCountryCode;
});

// Return current currency or return the default one set in the configuration
export const selectCurrentCurrency = createSelector(selectAuthenticationState, selectFromConfig('defaultCurrency', 'USD'), (state, defaultCurrency) =>
// $FlowFixMe[sketchy-null-string]
state.currencies?.current || defaultCurrency);

// Return the currency of user's prepaid, if any
export const selectUserCurrency = createSelector(selectAuthenticationState, state => state.currencies?.current);

// return whether user wants to display taxes including prices or not
export const selectTaxesDisplaySetting = createSelector(selectAuthenticationState, selectCurrent, (state, sharing) => sharing ? sharing.type === 'individual' : state.taxesIncluded ?? false);

/**
 * Action
 */

export const saveAuthenticatedUserInfos = createAction('Save authenticated user info');
export const saveUserCountryCode = createAction('Save user country code');
export const loadCurrenciesStart = createAction('Start Loading Currencies');
export const saveCurrencies = createAction('Save currencies');
export const saveCurrentCurrency = createAction('Save current currency');
export const loadCurrenciesFail = createAction('Load currencies fail');
export const saveTaxesDisplaySetting = createAction('Save user taxes display preference');

/**
 * Reducer
 */

export const initialState = {
  userInfos: undefined,
  currencies: {
    loading: false,
    current: '',
    list: []
  },
  countryCode: undefined
};
export default createReducer({
  /**
   * Save the user infos from the tokeninfo.
   */
  [saveAuthenticatedUserInfos]: (state, payload) => ({
    ...state,
    userInfos: {
      ...state.userInfos,
      ...payload
    }
  }),
  /**
   * Load currencies
   */
  [loadCurrenciesStart]: state => ({
    ...state,
    currencies: {
      loading: true
    }
  }),
  /**
   * Save currencies
   */
  [saveCurrencies]: (state, data) => ({
    ...state,
    currencies: {
      ...state.currencies,
      loading: false,
      list: data
    }
  }),
  /**
   * Save current currency
   */
  [saveCurrentCurrency]: (state, data) => ({
    ...state,
    currencies: {
      ...state.currencies,
      loading: false,
      current: data
    }
  }),
  /**
   * Load currencies fail. Save error.
   */
  [loadCurrenciesFail]: state => ({
    ...state,
    currencies: {
      ...state.currencies,
      loading: false
    }
  }),
  /**
   * Save country code.
   */
  [saveUserCountryCode]: (state, data) => ({
    ...state,
    countryCode: data
  }),
  [saveTaxesDisplaySetting]: (state, data) => ({
    ...state,
    taxesIncluded: data
  })
}, initialState);